<template>
	<div id="createDCWrapper" class="flex-grow flex flex-col" v-if="isUserAllowedInLevel5or6 && isAppContainer">
		<div v-show="!isLoading">
			<DamageAssessor
				v-show="pageIndex === pageIndexEnum.DamageAssessor"
				@next="next($event)"
				@previous="previous()"
				:is-skippable="false"
				:hide-back-btn="true"
				:start-value="apiData.damageAssessor"
				:is-new-case="false"
				:is-expertise-selected="apiData.workflowSelections.expertContract"
			/>
			<AncillaryServices v-show="pageIndex === pageIndexEnum.AncillaryServices" @next="next($event)" @previous="previous()" />
			<Client
				v-show="pageIndex === pageIndexEnum.Client"
				@next="next($event)"
				@previous="previous()"
				:is-skippable="false"
				:hide-back-btn="true"
				:start-value="apiData.customer"
				:is-new-case="false"
			/>
			<LawyerAuthority
				v-show="pageIndex === pageIndexEnum.LawyerAuthority"
				@next="next($event)"
				@previous="previous()"
				@clearLawyerOffice="clearLawyerOffice"
				:is-skippable="false"
				:title="$t('form_data_lawyers_office')"
			/>
			<ForeignLawyerOffice
				v-show="pageIndex === pageIndexEnum.ForeignLawyerOffice"
				@next="next($event)"
				@previous="previous()"
				:lawyer-office="apiData.lawyerOffice"
				:title="$t('form_data_lawyers_office')"
			/>
			<MailToLawyer
				v-show="pageIndex === pageIndexEnum.MailToLawyer"
				@next="next($event)"
				@previous="previous()"
				:api-data="apiData"
				:is-new-case="false"
			/>
			<RepairOrder
				v-show="pageIndex === pageIndexEnum.RepairOrder"
				@next="next($event)"
				@previous="previous()"
				:default-value="apiData.repairOrder"
				:show-back-button="getPageRegister()[0] !== pageIndexEnum.RepairOrder"
			/>
			<DrivingLicenseImage
				v-show="pageIndex === pageIndexEnum.DrivingLicenseImage"
				:show-back-button="getPageRegister()[0] !== pageIndexEnum.DrivingLicenseImage"
				@next="next($event)"
				@previous="previous()"
			/>
			<PersonalIDImage
				v-show="pageIndex === pageIndexEnum.PersonalIDImage"
				:show-back-button="getPageRegister()[0] !== pageIndexEnum.PersonalIDImage"
				@next="next($event)"
				@previous="previous()"
			/>
			<RentalCarContract
				v-show="pageIndex === pageIndexEnum.RentalCarContract"
				:display="pageIndex === pageIndexEnum.RentalCarContract"
				:damage-case="apiData"
				@next="next($event)"
				@previous="previous()"
				:show-back-button="getPageRegister()[0] !== pageIndexEnum.RentalCarContract"
				:cars="rentalCars"
			/>
			<AssessmentQuestions
				v-show="pageIndex === pageIndexEnum.AssessmentQuestions"
				@next="next($event)"
				@previous="previous()"
				:damage-case="apiData"
			/>
			<SignLocation
				v-show="pageIndex === pageIndexEnum.SignLocation"
				@next="next($event)"
				@previous="previous()"
				:default-city="defaultSignLocation"
				:show-back-button="getPageRegister()[0] !== pageIndexEnum.SignLocation"
			/>
			<SignCustomerService
				:is-shown="pageIndex === pageIndexEnum.SignCustomerService"
				v-show="pageIndex === pageIndexEnum.SignCustomerService"
				:is-rental-car-contract-later="apiData.workflowSelections.rentalCarContract"
				@next="next($event)"
				@previous="previous()"
			/>

			<SignConsultant
				:is-shown="pageIndex === pageIndexEnum.SignConsultant"
				v-show="pageIndex === pageIndexEnum.SignConsultant"
				@next="next($event)"
				@previous="previous()"
				:default-city="defaultSignLocation"
				:damage-case="apiData"
			/>

			<SignCompany
				:is-shown="pageIndex === pageIndexEnum.SignCompany"
				v-show="pageIndex === pageIndexEnum.SignCompany"
				@next="next($event)"
				@previous="previous()"
				:default-city="defaultSignLocation"
				:damage-case="apiData"
			/>

			<SignRentalCarContract
				:is-shown="pageIndex === pageIndexEnum.SignRentalCarContract"
				v-show="pageIndex === pageIndexEnum.SignRentalCarContract"
				@next="next($event)"
				@previous="previous()"
				:default-city="defaultSignLocation"
				:damage-case="apiData"
			/>

			<SignLawyer
				:is-shown="pageIndex === pageIndexEnum.SignLawyer"
				v-show="pageIndex === pageIndexEnum.SignLawyer"
				@next="next($event)"
				@previous="previous()"
				:default-city="defaultSignLocation"
				:damage-case="apiData"
			/>

			<Submit
				v-show="pageIndex === pageIndexEnum.Submit"
				:is-shown="pageIndex === pageIndexEnum.Submit"
				@next="next({ pdfUuids: $event.pdfUuids })"
				:damage-case="apiData"
				@previous="previous()"
			/>
			<Finish
				v-if="pageIndex === pageIndexEnum.Finish"
				:is-shown="pageIndex === pageIndexEnum.Finish"
				:damage-case="apiData"
				:has-customer-sign="evaluateCustomerSign()"
			/>
		</div>
		<LoadingSpinner v-show="isLoading" />
	</div>
	<Forbidden v-else />
</template>

<script>
import DamageAssessor from '@/components/damagecases/pages/DamageAssessor';
import LawyerAuthority from '@/components/damagecases/pages/LawyerAuthority';
import stateMerge from 'vue-object-merge';
import Submit from '@/components/damagecases/pages/Submit';
import { VehiclesApiFactory, CustomersApiFactory, VehicleApiFactory } from '@/api/generated';
import apiDefaults from '@/api/defaultConfiguration';
import MailToLawyer from '@/components/damagecases/pages/MailToLawyer';
import ForeignLawyerOffice from '@/components/damagecases/pages/ForeignLawyerOffice';
import RepairOrder from '@/components/damagecases/pages/RepairOrder.vue';
import DrivingLicenseImage from '@/components/damagecases/pages/DrivingLicenseImage.vue';
import PersonalIDImage from '@/components/damagecases/pages/PersonalIDImage.vue';
import RentalCarContract from '@/components/damagecases/pages/RentalCarContract.vue';
import SignLocation from '@/components/damagecases/pages/SignLocation.vue';
import SignCustomerService from '@/components/damagecases/pages/SignCustomerService.vue';
import SignConsultant from '@/components/damagecases/pages/SignConsultant.vue';
import SignCompany from '@/components/damagecases/pages/SignCompany.vue';
import SignRentalCarContract from '@/components/damagecases/pages/SignRentalCarContract.vue';
import SignLawyer from '@/components/damagecases/pages/SignLawyer.vue';
import Finish from '@/components/damagecases/pages/Finish.vue';
import Client from '@/components/damagecases/pages/Client.vue';
import Authentication from '@/mixins/Authentication';
import AppContainerMixin from '@/mixins/AppContainerMixin';
import Forbidden from '@/views/default/Forbidden.vue';
import LoadingSpinner from '@/components/navigation/LoadingSpinner.vue';
import AncillaryServices from '@/components/damagecases/pages/AncillaryServices.vue';
import AssessmentQuestions from '@/components/damagecases/pages/AssessmentQuestions.vue';

const vehicleApi = VehicleApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios);
const vehiclesApi = VehiclesApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios);
const customersApi = CustomersApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios);

export default {
	name: 'DamagecaseCreateDocuments',
	components: {
		AncillaryServices,
		AssessmentQuestions,
		LoadingSpinner,
		Forbidden,
		Client,
		Finish,
		SignLawyer,
		SignRentalCarContract,
		SignCompany,
		SignConsultant,
		SignCustomerService,
		SignLocation,
		RentalCarContract,
		PersonalIDImage,
		DrivingLicenseImage,
		RepairOrder,
		ForeignLawyerOffice,
		MailToLawyer,
		Submit,
		LawyerAuthority,
		DamageAssessor,
	},
	mixins: [LoadingSpinner, Authentication, AppContainerMixin],
	mounted() {
		if (this.isUserAllowedInLevel5or6 && this.isAppContainer) {
			this.isLoading = true;
			this.$store.commit('setConfirmLeaving', this.$t('leave_page'));
			this.me = this.$store.getters.getUser;

			// Wenn Customer übergeben wurde, müssen seine Fahrzeuge ausgelesen werden
			if (this.$route.params.customer || this.$route.query.customer_id) {
				customersApi.customersIdGet(parseInt(this.$route.params.customer || this.$route.query.customer_id)).then(response => {
					this.apiData.customer = response.data;
					this.getCustomerCars();
				});
			} else {
				this.isLoading = false;
			}
			this.apiData.workflowSelections = this.$route.params.workflowSelection;
			this.defaultSignLocation = this.me.person.company?.city.name;
			if (!this.defaultSignLocation) this.defaultSignLocation = '';
			this.getRentalCars();
			this.apiData.signLocation = this.defaultSignLocation;

			const damageCase = this.$store.getters.getDamageCase;
			this.isOrderDocumentationOrComprehensive = this.$store.getters.isProcessOrderDocumentation || this.$store.getters.isProcessComprehensive;
			this.apiData.id = damageCase.id;
			this.apiData.customer = damageCase.customer;
			this.apiData.repairOrder = damageCase.hasCostEstimation ? this.$t('repair_billing_estimate') : this.$t('repair_billing_expertise');
			this.apiData.damageAssessor = damageCase.damageAssessor === undefined ? null : damageCase.damageAssessor;
			this.apiData.damageAssessorCompany = damageCase.damageAssessorCompany === undefined ? null : damageCase.damageAssessorCompany;
			this.apiData.imdNetId = damageCase.imdNetId;
			this.apiData.replacementRentalCarClass = damageCase.replacementRentalCarClass;
			this.hasAssessor = this.checkHasAssessor(damageCase);
			this.apiData.lawyerOffice = damageCase.lawyerOffice === undefined ? {} : damageCase.lawyerOffice;
			this.apiData.oppositeLicensePlate = damageCase.oppositeLicensePlate;
			this.hasLawyersOffice = damageCase.lawyerOffice !== undefined;
			this.pageRegister = this.getPageRegister();
			this.pageIndex = this.pageRegister[this.pageRegisterIndex];
		}
	},
	watch: {
		pageIndex: function() {
			this.$emit('scrollUp', this.pageIndex);
		},
	},
	data() {
		return {
			me: {},
			customerCars: [],
			rentalCars: [],
			defaultSignLocation: '',
			pageRegister: [],
			pageRegisterIndex: 0,
			pageIndexEnum: {
				RentalCarContract: 5,
				Finish: 6,
				SignLocation: 9,
				AncillaryServices: 2,
				DrivingLicenseImage: 10,
				PersonalIDImage: 11,
				RepairOrder: 8,
				AssessmentQuestions: 12,
				SignCustomerService: 14,
				SignConsultant: 15,
				SignCompany: 16,
				SignLawyer: 17,
				Submit: 18,
				OppositePerson: 19,
				SignRentalCarContract: 24,
				LawyerAuthority: 3,
				MailToLawyer: 7,
				Client: 0,
				DamageAssessor: 1,
				ForeignLawyerOffice: 4,
			},
			pageIndex: 0,
			hasLawyersOffice: false,
			hasAssessor: false,
			isOrderDocumentationOrComprehensive: false,
			apiData: {
				id: null,
				repairOrder: this.$t('repair_billing_expertise'),
				isPersonInjured: null,
				isRecordedByPolice: null,
				date: null,
				accidentLocation: null,
				workflowSelections: {
					expertContract: true,
					lawyerAuthority: false,
					paymentOrder: false,
					repairContract: false,
					rentalCarContract: false,
					paymentAssignment: false,
					nebenleistungen: false,
				},
				damageAssessor: {},
				damageAssessorNonSystemPartner: false,
				damageAssessorCompany: {},
				opposite_case_number: null,
				opposite_insurance_contract_number: null,
				customer: {
					id: null,
					gender: null,
					title: null,
					firstName: null,
					lastName: null,
					street: null,
					houseNumber: null,
					city: {
						id: null,
					},
					phoneNumber: null,
					email: null,
				},
				vehicle: {
					vehicleIdentificationNumber: null,
					manufacturer: null,
					model: null,
					licensePlate: {
						isSpecial: false,
						text: null,
					},
					yearOfConstruction: null,
					numberOfPreviousOwners: null,
				},
				oppositePerson: {
					title: null,
					firstName: null,
					lastName: null,
					street: null,
					houseNumber: null,
					city: {
						id: null,
					},
					phoneNumber: null,
					email: null,
				},
				oppositeLicensePlate: {
					isSpecial: false,
					text: null,
				},
				lawyerOffice: {
					id: null,
					name: null,
					houseNumber: null,
					street: null,
					phoneNumber: null,
					email: null,
					city: {
						id: null,
					},
				},
				lawyerOfficeReferenceNumber: null,
				rentalCarContract: {
					isDriverLicenseChecked: false,
					isIdentificationCardChecked: false,
					rentStartDate: null,
					kilometrage: null,
					vehicle: {
						id: null,
					},
				},
				customerConsultant: {
					id: null,
				},
				repairShop: {
					id: null,
				},
				signLocation: null,
				//images
				overviewImage: null,
				personalIDImage: null,
				drivingLicenseImage: null,
				policeReportImage: null,
				pdfUuids: [],
				assessmentQuestions: {  },
			},
			vehicleRegistrationImageContent: null,
			isLoading: false,
		};
	},
	methods: {
		checkHasAssessor(damageCase) {
			if (damageCase.damageAssessor) {
				return true;
			} else if (damageCase.damageAssessorCompany) {
				return true;
			} else return false;
		},
		evaluateCustomerSign() {
			if (
				this.apiData.consultantSign === undefined &&
				this.apiData.lawyerSign === undefined &&
				this.apiData.companySign === undefined &&
				this.apiData.signRentalCarContract === undefined
			) {
				return false;
			} else {
				return true;
			}
		},
		clearCustomer() {
			this.apiData.customer = {
				id: null,
				gender: null,
				title: null,
				firstName: null,
				lastName: null,
				street: null,
				houseNumber: null,
				city: {
					id: null,
					zipCode: null,
					name: null,
				},
				phoneNumber: null,
				email: null,
				company: {
					name: null,
				},
			};
			this.customerCars = [];
		},
		clearLawyerOffice() {
			this.apiData.lawyerOffice = { city: {} };
		},
		getCustomerCars() {
			vehiclesApi
				.vehiclesCustomerIdGet(this.apiData.customer.id)
				.then(result => {
					this.customerCars = result.data;

					if (this.$route.query.vehicle_id) {
						this.apiData.vehicle = this.customerCars.find(car => car.id === parseInt(this.$route.query.vehicle_id));
					}
					this.isLoading = false;
				})
				.catch(error => {});
		},
		getRentalCars() {
			vehicleApi
				.rentalcarsGet()
				.then(result => {
					this.rentalCars = result.data.data;
				})
				.catch(error => {});
		},
		next(args) {
			if (args?.workflowSelections !== undefined) {
				if (!args.workflowSelections.rentalCarContract) {
					this.apiData.drivingLicenseImage = null;
					this.apiData.personalIDImage = null;
				}
			}
			// Wenn keine Daten übergeben werden, wurde die Seite übersprungen
			if (args) {
				stateMerge(this.apiData, args);
			}
			this.$emit('scrollUp');
			this.pageRegister = this.getPageRegister();

			// Prüfen ob Customer übergeben wurde. Wenn ja, dann muss die Reihenfolge beim Kundenfahrzeug geändert werden wenn Kunde bereits Fahrzeuge hat
			this.pageIndex = this.pageRegister[++this.pageRegisterIndex];
		},
		previous() {
			this.pageRegister = this.getPageRegister();
			const index = this.pageRegister[--this.pageRegisterIndex];
			if (index || index === 0) this.pageIndex = index;
		},
		checkNeedsCustomerSign() {
			return this.defaultSignLocation === this.apiData.signLocation;
		},
		getPageRegister() {
			const register = [];
			register.push(this.pageIndexEnum.Client);
			if (!this.isOrderDocumentationOrComprehensive) {
				if (!this.hasAssessor) {
					if (this.apiData.workflowSelections.expertContract || this.apiData.workflowSelections.paymentOrder) {
						register.push(this.pageIndexEnum.DamageAssessor);
					}
				} else {
					this.apiData.damageAssessorNonSystemPartner = this.apiData.damageAssessor && !this.apiData.damageAssessor.system_assessor;
				}

				if (this.apiData.workflowSelections.expertContract) {
					register.push(this.pageIndexEnum.AssessmentQuestions);
				}

				if (!this.hasLawyersOffice) {
					if (this.apiData.workflowSelections.lawyerAuthority || this.apiData.workflowSelections.paymentOrder) {
						register.push(this.pageIndexEnum.LawyerAuthority);
						if (this.apiData.lawyerOffice.id === null) {
							register.push(this.pageIndexEnum.ForeignLawyerOffice);
						}
						if (!this.apiData.lawyerOffice.isEditable) {
							register.push(this.pageIndexEnum.MailToLawyer);
						}
					} else {
						this.apiData.lawyerOffice = null;
					}
				}
			} else {
				this.apiData.lawyerOffice = null;
				this.apiData.damageAssessor = null;
			}

			if (this.apiData.workflowSelections.repairContract) {
				register.push(this.pageIndexEnum.RepairOrder);
			}

			if (this.apiData.workflowSelections.nebenleistungen) {
				register.push(this.pageIndexEnum.AncillaryServices);
			}

			if (this.apiData.workflowSelections.rentalCarContract) {
				register.push(this.pageIndexEnum.PersonalIDImage);
				register.push(this.pageIndexEnum.DrivingLicenseImage);
				register.push(this.pageIndexEnum.RentalCarContract);
			}
			register.push(this.pageIndexEnum.SignLocation);

			if (
				this.apiData.workflowSelections.repairContract ||
				this.apiData.workflowSelections.rentalCarContract ||
				this.apiData.workflowSelections.paymentOrder ||
				this.apiData.workflowSelections.nebenleistungen
			) {
				register.push(this.pageIndexEnum.SignCustomerService);
			}

			if (this.apiData.workflowSelections.expertContract && !this.apiData.damageAssessorNonSystemPartner && this.checkNeedsCustomerSign()) {
				register.push(this.pageIndexEnum.SignConsultant);
			}

			if (
				(this.apiData.workflowSelections.repairContract ||
					this.apiData.workflowSelections.paymentAssignment ||
					this.apiData.workflowSelections.nebenleistungen) &&
				this.checkNeedsCustomerSign()
			) {
				register.push(this.pageIndexEnum.SignCompany);
			}

			if (this.apiData.workflowSelections.rentalCarContract && this.checkNeedsCustomerSign()) {
				register.push(this.pageIndexEnum.SignRentalCarContract);
			}

			if ((this.apiData.workflowSelections.paymentOrder || this.apiData.workflowSelections.lawyerAuthority) && this.checkNeedsCustomerSign()) {
				register.push(this.pageIndexEnum.SignLawyer);
			}

			register.push(this.pageIndexEnum.Submit);
			register.push(this.pageIndexEnum.Finish);

			return register;
		},
	},
};
</script>

<style>
#createDCWrapper > div {
	@apply flex-grow flex flex-col relative;
}
</style>
